/**
 * @file EmployeeLoginHistoryDialog.tsx
 * @brief Company-admin login-history modal — thin wrapper over the shared dialog.
 *
 * @details
 * Owns the query (`useEmployeeLoginHistory` →
 * GET /api/company/users/:id/login-history) and hands the result to the shared
 * presentational `LoginHistoryDialog`. Company-scoped on the backend.
 *
 * @author DES
 * @date 08-07-2026
 */

import { LoginHistoryDialog as SharedLoginHistoryDialog } from "@/shared/components/LoginHistoryDialog";
import { useEmployeeLoginHistory } from "../api";
import type { CompanyEmployee } from "../schemas";

interface EmployeeLoginHistoryDialogProps {
  employee: CompanyEmployee | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmployeeLoginHistoryDialog({
  employee,
  open,
  onOpenChange,
}: EmployeeLoginHistoryDialogProps) {
  const { data, isLoading, isError } = useEmployeeLoginHistory(
    employee?.id ?? null,
    open,
  );

  const name =
    [employee?.first_name, employee?.last_name].filter(Boolean).join(" ") ||
    employee?.email ||
    "employee";

  return (
    <SharedLoginHistoryDialog
      name={name}
      open={open}
      onOpenChange={onOpenChange}
      history={data}
      isLoading={isLoading}
      isError={isError}
    />
  );
}
